import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/auth/AuthContext';
import { format } from 'date-fns';
import TimeLogCalendar from './TimeLogCalendar';

interface TimeLog {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  status: string;
  notes: string | null;
}

const TimeLogHistory: React.FC = () => {
  const { userProfile } = useAuth();
  const [logs, setLogs] = useState<TimeLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalHours, setTotalHours] = useState(0);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');

  useEffect(() => {
    fetchLogs();
  }, [userProfile]);

  // Setup realtime subscription for time log changes
  useEffect(() => {
    if (!userProfile?.id) return;

    const setupRealtimeSubscription = async () => {
      // Get staff record
      const { data: staffData } = await supabase
        .from('team')
        .select('id')
        .eq('user_id', userProfile.id)
        .single();

      if (!staffData) return;

      const channel = supabase
        .channel('crew-time-logs-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'crew_time_logs',
            filter: `staff_id=eq.${staffData.id}`
          },
          (payload) => {
            console.log('📡 Time log change detected:', payload);
            // Refresh logs when any change occurs
            fetchLogs();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    const cleanup = setupRealtimeSubscription();
    return () => {
      cleanup.then(cleanupFn => cleanupFn && cleanupFn());
    };
  }, [userProfile]);

  const fetchLogs = async () => {
    if (!userProfile?.id) return;

    try {
      // Get staff record
      const { data: staffData } = await supabase
        .from('team')
        .select('id')
        .eq('user_id', userProfile.id)
        .single();

      if (!staffData) return;

      // Get time logs for the past 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('crew_time_logs')
        .select('*')
        .eq('staff_id', staffData.id)
        .gte('clock_in_time', thirtyDaysAgo.toISOString())
        .order('clock_in_time', { ascending: false });

      if (error) {
        console.error('Error fetching logs:', error);
        return;
      }

      setLogs(data || []);

      // Calculate total hours
      const total = (data || []).reduce((sum, log) => {
        if (log.clock_out_time) {
          const start = new Date(log.clock_in_time).getTime();
          const end = new Date(log.clock_out_time).getTime();
          const hours = (end - start) / (1000 * 60 * 60);
          return sum + hours;
        }
        return sum;
      }, 0);

      setTotalHours(total);
    } catch (err) {
      console.error('Error in fetchLogs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return format(date, 'MMM d, yyyy h:mm a');
  };

  const calculateDuration = (start: string, end: string | null) => {
    if (!end) return 'In Progress';
    
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    const diff = endTime - startTime;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Time Log History</h2>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#3A5E63]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <h2 className="text-xl font-semibold text-[#3A5E63]">Time Log History</h2>
        <div className="flex items-center gap-3">
          <div className="text-sm">
            <span className="text-gray-600">Total (30 days): </span>
            <span className="font-semibold text-[#3A5E63]">{totalHours.toFixed(1)} hours</span>
          </div>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1 text-sm font-medium transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-[#3A5E63] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              📅 Calendar
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-[#3A5E63] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              📋 List
            </button>
          </div>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No time logs found
        </div>
      ) : viewMode === 'calendar' ? (
        <TimeLogCalendar logs={logs} />
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {logs.map((log) => (
            <div
              key={log.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-700">Clock In:</span>
                    <span className="text-sm text-gray-900">{formatDateTime(log.clock_in_time)}</span>
                  </div>
                  {log.clock_out_time ? (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-700">Clock Out:</span>
                      <span className="text-sm text-gray-900">{formatDateTime(log.clock_out_time)}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      <span className="text-sm font-medium text-green-600">Currently Clocked In</span>
                    </div>
                  )}
                  {log.notes && (
                    <div className="text-xs text-gray-500 mt-2">
                      Note: {log.notes}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className={`text-sm font-semibold ${log.clock_out_time ? 'text-[#3A5E63]' : 'text-green-600'}`}>
                    {calculateDuration(log.clock_in_time, log.clock_out_time)}
                  </div>
                  <div className={`text-xs mt-1 ${log.status === 'clocked_in' ? 'text-green-600' : 'text-gray-500'}`}>
                    {log.status === 'clocked_in' ? 'Active' : 'Completed'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TimeLogHistory;



